import { Grid2 as Grid, Typography } from '@mui/material'
import Paper from 'components/@extended/Paper'
import dayjs from 'dayjs'
import { useCreatePlanContext } from '../../contexts/CreatePlanContextProvider'
import { createIdLabelMap, DATE_FORMAT } from 'types/periods'
import { currencyFormatter, twoDigitPercentFormatter } from 'utils/formatting'

function WizardCompensationHeader() {
  const { plan, employeeName, compensationTemplate } = useCreatePlanContext()

  const baseSalary = plan.baseSalary || 0
  const variablePercent = plan.variablePercent || 0
  const periodProration = plan.periodProration || 1.0
  const compensationModel = plan.compensationModel || 'BASE_PLUS'
  const variableCap = plan.variableCap || 0

  let periodLabel = ''
  if (compensationTemplate?.period) {
    const periodIdLabelMap = createIdLabelMap(compensationTemplate.period)
    periodLabel = periodIdLabelMap[plan.periodId] || ''
  }

  const baseCompensation = baseSalary * periodProration
  const variableCompensation = baseSalary * variablePercent * periodProration
  const maxVariableCompensation = baseSalary * variableCap * variablePercent * periodProration
  const onTargetTotal =
    compensationModel === 'BASE_PLUS'
      ? baseCompensation + variableCompensation
      : baseSalary * periodProration * (1 - variablePercent) + variableCompensation
  const maxTotal =
    compensationModel === 'BASE_PLUS'
      ? baseCompensation + maxVariableCompensation
      : baseSalary * periodProration * (1 - variablePercent) + maxVariableCompensation

  // Don't render if missing required data
  if (!employeeName || !plan.baseSalary || !plan.variablePercent) return null

  return (
    <Grid container spacing={3}>
      {/* Employee Information Bar */}
      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 3 }} sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Employee Name
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {employeeName}
              </Typography>
            </Grid>

            <Grid size={{ xs: 6, sm: 3 }} sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Position
              </Typography>
              <Typography variant="body1">{plan.description}</Typography>
            </Grid>

            <Grid size={{ xs: 6, sm: 3 }} sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Compensation Period
              </Typography>
              <Typography variant="body1">{periodLabel}</Typography>
            </Grid>

            <Grid size={{ xs: 6, sm: 3 }} sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Effective Date
              </Typography>
              <Typography variant="body1">
                {plan.effectiveDate ? dayjs.utc(plan.effectiveDate).format(DATE_FORMAT) : ''}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Three Outlined Compensation Cards */}
      <Grid size={{ xs: 12, sm: 4 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            textAlign: 'center',
            height: 140,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ height: '20px' }}>
            Base Compensation
          </Typography>
          <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h4" fontWeight="bold">
              {currencyFormatter.format(baseCompensation)}
            </Typography>
          </div>
          <div style={{ height: '28px' }}></div>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, sm: 4 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            textAlign: 'center',
            height: 140,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ height: '20px' }}>
            Variable Compensation
          </Typography>
          <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h4" fontWeight="bold">
              {currencyFormatter.format(variableCompensation)}
            </Typography>
          </div>
          <div style={{ height: '28px' }}>
            <Typography variant="caption" color="text.secondary" display="block">
              ({twoDigitPercentFormatter.format(variablePercent)})
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Up to {currencyFormatter.format(maxVariableCompensation)}
            </Typography>
          </div>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, sm: 4 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            textAlign: 'center',
            height: 140,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ height: '20px' }}>
            On-Target Total
          </Typography>
          <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h4" fontWeight="bold" color="primary.main">
              {currencyFormatter.format(onTargetTotal)}
            </Typography>
          </div>
          <div style={{ height: '28px' }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Up to {currencyFormatter.format(maxTotal)}
            </Typography>
          </div>
        </Paper>
      </Grid>
    </Grid>
  )
}

export default WizardCompensationHeader
